import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { TILE_REGISTRY } from '../config/tileRegistry';

const Home = () => {
  const navigate = useNavigate();
  const { logout, userAttributes } = useAuth();

  const tiles = (userAttributes?.tiles || [])
    .map((key) => TILE_REGISTRY[key])
    .filter(Boolean);

  return (
    <Container>
      <Header>
        <LogoSection>
          <Logo>
            KAIWA <AIText>AI</AIText>
          </Logo>
        </LogoSection>
        <UserSection>
          <UserEmail>{userAttributes?.email}</UserEmail>
          <LogoutButton onClick={logout}>Logout</LogoutButton>
        </UserSection>
      </Header>

      <MainContent>
        <WelcomeSection>
          <WelcomeTitle>Welcome to Kaiwa Survey AI Insights Platform</WelcomeTitle>
          <WelcomeDes>
            Explore comprehensive survey analytics, monitor real-time metrics, and interact with your research data using our AI-powered platform.
          </WelcomeDes>
        </WelcomeSection>

        <TilesGrid>
          {tiles.map((tile) => (
            <TileCard
              key={tile.id}
              onClick={() => navigate(tile.path)}
              color={tile.color}
            >
              <TileIcon color={tile.color}>{tile.icon}</TileIcon>
              <TileTitle>{tile.title}</TileTitle>
              <TileDescription>{tile.description}</TileDescription>
              <TileArrow>→</TileArrow>
            </TileCard>
          ))}
        </TilesGrid>

        <Footer>
          <FooterText>
            © 2025 Alytico AI. All rights reserved. | KAIWA Survey AI Platform v1.0
          </FooterText>
        </Footer>
      </MainContent>
    </Container>
  );
};

export default Home;

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background-color: ${(props) => props.theme.colors.background};
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  background: linear-gradient(135deg, ${(props) => props.theme.colors.primary} 0%, ${(props) => props.theme.colors.primaryDark} 100%);
  padding: ${(props) => props.theme.spacing.lg} ${(props) => props.theme.spacing.xl};
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: ${(props) => props.theme.shadows.md};
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${(props) => props.theme.spacing.md};
    padding: ${(props) => props.theme.spacing.md};
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${(props) => props.theme.spacing.sm};
`;

const Logo = styled.h1`
  font-family: ${(props) => props.theme.fonts.heading};
  font-size: ${(props) => props.theme.fontSizes['4xl']};
  font-weight: ${(props) => props.theme.fontWeights.bold};
  color: ${(props) => props.theme.colors.textPrimary};
  letter-spacing: 2px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AIText = styled.span`
  color: #E53935;
  font-size: ${(props) => props.theme.fontSizes['4xl']};
  font-weight: ${(props) => props.theme.fontWeights.bold};
  letter-spacing: 2px;
`;

const Subtitle = styled.span`
  font-size: ${(props) => props.theme.fontSizes.lg};
  font-weight: ${(props) => props.theme.fontWeights.light};
  color: ${(props) => props.theme.colors.textPrimary};
  opacity: 0.9;
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.md};
`;

const UserEmail = styled.span`
  font-size: ${(props) => props.theme.fontSizes.sm};
  color: ${(props) => props.theme.colors.textPrimary};
  opacity: 0.9;
`;

const LogoutButton = styled.button`
  padding: ${(props) => props.theme.spacing.sm} ${(props) => props.theme.spacing.md};
  background-color: rgba(255, 255, 255, 0.1);
  color: ${(props) => props.theme.colors.textPrimary};
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${(props) => props.theme.borderRadius.md};
  font-size: ${(props) => props.theme.fontSizes.sm};
  font-weight: ${(props) => props.theme.fontWeights.medium};
  cursor: pointer;
  transition: all ${(props) => props.theme.transitions.fast};
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
  }
`;

const MainContent = styled.main`
  flex: 1;
  max-width: 1400px;
  margin: 0 auto;
  padding: ${(props) => props.theme.spacing['3xl']} ${(props) => props.theme.spacing.xl};
  width: 100%;
  
  @media (max-width: 768px) {
    padding: ${(props) => props.theme.spacing.xl} ${(props) => props.theme.spacing.md};
  }
`;

const WelcomeSection = styled.div`
  text-align: center;
  margin-bottom: ${(props) => props.theme.spacing['3xl']};
`;

const WelcomeTitle = styled.h2`
  font-family: ${(props) => props.theme.fonts.heading};
  font-size: ${(props) => props.theme.fontSizes['4xl']};
  font-weight: ${(props) => props.theme.fontWeights.bold};
  color: ${(props) => props.theme.colors.textPrimary};
  margin-bottom: ${(props) => props.theme.spacing.md};
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: ${(props) => props.theme.fontSizes['2xl']};
  }
`;

const WelcomeDes = styled.p`
  font-size: ${(props) => props.theme.fontSizes.lg};
  color: ${(props) => props.theme.colors.textSecondary};
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: ${(props) => props.theme.fontSizes.base};
  }
`;

const TilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: ${(props) => props.theme.spacing.xl};
  margin-bottom: ${(props) => props.theme.spacing['3xl']};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${(props) => props.theme.spacing.lg};
  }
`;

const TileCard = styled.div`
  background: ${(props) => props.theme.colors.cardBackground};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borderRadius.xl};
  padding: ${(props) => props.theme.spacing.xl};
  cursor: pointer;
  transition: all ${(props) => props.theme.transitions.medium};
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${(props) => props.color} 0%, ${(props) => props.theme.colors.primary} 100%);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform ${(props) => props.theme.transitions.medium};
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: ${(props) => props.theme.shadows.teal};
    border-color: ${(props) => props.color};
    
    &:before {
      transform: scaleX(1);
    }
  }
`;

const TileIcon = styled.div`
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, ${(props) => props.color} 0%, ${(props) => props.theme.colors.primaryDark} 100%);
  border-radius: ${(props) => props.theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${(props) => props.theme.spacing.lg};
  
  svg {
    font-size: 32px;
    color: ${(props) => props.theme.colors.textPrimary};
  }
`;

const TileTitle = styled.h3`
  font-family: ${(props) => props.theme.fonts.heading};
  font-size: ${(props) => props.theme.fontSizes.xl};
  font-weight: ${(props) => props.theme.fontWeights.semibold};
  color: ${(props) => props.theme.colors.textPrimary};
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const TileDescription = styled.p`
  font-size: ${(props) => props.theme.fontSizes.base};
  color: ${(props) => props.theme.colors.textSecondary};
  line-height: 1.6;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const TileArrow = styled.span`
  font-size: ${(props) => props.theme.fontSizes['2xl']};
  color: ${(props) => props.theme.colors.primary};
  font-weight: ${(props) => props.theme.fontWeights.bold};
  display: inline-block;
  transition: transform ${(props) => props.theme.transitions.fast};
  
  ${TileCard}:hover & {
    transform: translateX(8px);
  }
`;

const Footer = styled.footer`
  text-align: center;
  padding-top: ${(props) => props.theme.spacing.xl};
  border-top: 1px solid ${(props) => props.theme.colors.divider};
`;

const FooterText = styled.p`
  font-size: ${(props) => props.theme.fontSizes.sm};
  color: ${(props) => props.theme.colors.textTertiary};
`;
